import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, Map, Upload, Trash2, MapPin, Plus, X, DoorClosed,
} from "lucide-react";
import { plansApi, buildingsApi, doorsApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { apiError } from "@/lib/format";
import { useAuth, userHasRole } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const PLAN_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function UploadPlanDialog({ open, onOpenChange, buildingId, onUploaded }) {
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (!open) { setFile(null); setLabel(""); } }, [open]);

  const submit = async () => {
    if (!file) { toast.error("Choose a plan file."); return; }
    if (!label.trim()) { toast.error("Give the plan a label."); return; }
    setSaving(true);
    try {
      const b64 = await fileToBase64(file);
      await plansApi.create(buildingId, {
        label: label.trim(),
        file_name: file.name,
        mime_type: file.type || "application/pdf",
        file_base64: b64,
      });
      toast.success("Floor plan uploaded");
      onUploaded();
    } catch (e) { toast.error(apiError(e, "Upload failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload floor plan</DialogTitle>
          <DialogDescription>PDF or image. You can pin doors onto image plans afterwards.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Label *</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Ground floor" data-testid="plan-label-input" />
          </div>
          <div className="space-y-2">
            <Label>File *</Label>
            <input ref={inputRef} type="file" accept={PLAN_ACCEPT} className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="plan-file-input" />
            <Button variant="outline" className="w-full justify-start" onClick={() => inputRef.current?.click()} data-testid="plan-choose-file">
              <Upload className="mr-2 h-4 w-4" /> {file ? file.name : "Choose file…"}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="plan-upload-btn">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlanViewer({ buildingId, plan, doors, canManage, onPinsChanged }) {
  const full = useAsync(() => plansApi.getOne(buildingId, plan.id), [buildingId, plan.id]);
  const [pins, setPins] = useState(plan.pins || []);
  const [placing, setPlacing] = useState(false);
  const [pinDoorId, setPinDoorId] = useState("");
  const [saving, setSaving] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => { setPins(plan.pins || []); }, [plan.id, plan.pins]);

  const doorMap = useMemo(() => Object.fromEntries((doors || []).map((d) => [d.id, d])), [doors]);
  const isImage = String(plan.mime_type || "").startsWith("image/");
  const dataUrl = full.data?.file_base64 ? `data:${plan.mime_type};base64,${full.data.file_base64}` : null;

  const savePins = async (next) => {
    setSaving(true);
    try {
      await plansApi.patch(buildingId, plan.id, { pins: next });
      setPins(next);
      onPinsChanged();
    } catch (e) { toast.error(apiError(e, "Could not save pins")); }
    finally { setSaving(false); }
  };

  const onImageClick = (e) => {
    if (!placing || !pinDoorId || !imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    const next = [...pins.filter((p) => p.door_id !== pinDoorId), { door_id: pinDoorId, x, y }];
    savePins(next);
    setPlacing(false);
    setPinDoorId("");
  };

  const removePin = (doorId) => savePins(pins.filter((p) => p.door_id !== doorId));

  return (
    <Card className="p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-semibold text-slate-800"><Map className="h-5 w-5 text-primary" /> {plan.label}</h3>
        {canManage && isImage && (
          <div className="flex items-center gap-2">
            {placing ? (
              <>
                <Select value={pinDoorId} onValueChange={setPinDoorId}>
                  <SelectTrigger className="h-9 w-48" data-testid="pin-door-select"><SelectValue placeholder="Pick a door" /></SelectTrigger>
                  <SelectContent>
                    {(doors || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.door_reference}{d.location ? ` · ${d.location}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => { setPlacing(false); setPinDoorId(""); }} data-testid="pin-cancel-btn">Cancel</Button>
              </>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setPlacing(true)} data-testid="pin-add-btn"><Plus className="mr-1.5 h-4 w-4" /> Pin a door</Button>
            )}
          </div>
        )}
      </div>

      {placing && <p className="mb-2 text-sm text-primary">Select a door, then click its location on the plan.</p>}
      {saving && <p className="mb-2 flex items-center gap-1.5 text-sm text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</p>}

      {full.loading && <LoadingState label="Loading plan…" rows={1} />}
      {full.error && <ErrorState message={full.error} onRetry={full.reload} />}

      {!full.loading && !full.error && dataUrl && (
        isImage ? (
          <div className={`relative inline-block max-w-full overflow-hidden rounded-lg border border-slate-200 ${placing ? "cursor-crosshair" : ""}`}>
            <img ref={imgRef} src={dataUrl} alt={plan.label} className="max-h-[70vh] w-auto max-w-full select-none" onClick={onImageClick} data-testid="plan-image" />
            {pins.map((p) => {
              const d = doorMap[p.door_id];
              return (
                <div key={p.door_id} className="group absolute -translate-x-1/2 -translate-y-full" style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }} data-testid={`plan-pin-${p.door_id}`}>
                  <div className="flex flex-col items-center">
                    <span className="whitespace-nowrap rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-semibold text-white opacity-0 shadow transition-opacity group-hover:opacity-100">
                      {d?.door_reference || "Door"}
                    </span>
                    <MapPin className="h-6 w-6 fill-red-500/20 text-red-600 drop-shadow" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <iframe title={plan.label} src={dataUrl} className="h-[70vh] w-full rounded-lg border border-slate-200" data-testid="plan-pdf" />
        )
      )}

      {!full.loading && !full.error && !dataUrl && (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center text-sm text-slate-400" data-testid="plan-no-file">
          The plan image is unavailable. Re-upload the plan file to view it.
        </div>
      )}

      {pins.length > 0 && (
        <div className="mt-4">
          <h4 className="mb-2 text-sm font-semibold text-slate-700">Pinned doors ({pins.length})</h4>
          <div className="flex flex-wrap gap-2">
            {pins.map((p) => {
              const d = doorMap[p.door_id];
              return (
                <span key={p.door_id} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 py-1 pl-3 pr-1.5 text-sm text-slate-700" data-testid={`pin-chip-${p.door_id}`}>
                  <DoorClosed className="h-3.5 w-3.5 text-slate-400" />
                  {d ? <Link to={`/doors/${d.id}`} className="hover:underline">{d.door_reference}</Link> : "Unknown door"}
                  {canManage && (
                    <button onClick={() => removePin(p.door_id)} className="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-red-600" data-testid={`pin-remove-${p.door_id}`}><X className="h-3.5 w-3.5" /></button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function FloorPlans() {
  const { id: buildingId } = useParams();
  const { user } = useAuth();
  const canManage = user?.is_company_admin || userHasRole(user, "admin", "inspector");
  const building = useAsync(() => buildingsApi.getOne(buildingId), [buildingId]);
  const doors = useAsync(() => doorsApi.getAll(buildingId), [buildingId]);
  const { data, loading, error, reload } = useAsync(() => plansApi.list(buildingId), [buildingId]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const plans = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  useEffect(() => { if (plans.length && !activeId) setActiveId(plans[0].id); }, [plans, activeId]);

  const active = plans.find((p) => p.id === activeId) || plans[0] || null;

  const deletePlan = async (planId) => {
    if (!window.confirm("Delete this plan?")) return;
    try { await plansApi.remove(buildingId, planId); toast.success("Plan deleted"); setActiveId(null); reload(); }
    catch (e) { toast.error(apiError(e, "Could not delete")); }
  };

  return (
    <div>
      <Link to={`/buildings/${buildingId}`} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> {building.data?.name || "Building"}
      </Link>
      <PageHeader
        title="Floor plans"
        subtitle="See where each fire door lives in the building"
        testId="plans-header"
        actions={canManage ? <Button onClick={() => setDialogOpen(true)} data-testid="plans-upload-btn"><Upload className="mr-1.5 h-4 w-4" /> Upload plan</Button> : null}
      />

      {loading && <LoadingState label="Loading plans…" />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && (
        plans.length === 0 ? (
          <EmptyState title="No floor plans" message="Upload a floor plan to map your fire doors onto the building layout." testId="plans-empty" action={canManage ? <Button onClick={() => setDialogOpen(true)}><Upload className="mr-1.5 h-4 w-4" /> Upload plan</Button> : null} />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2" data-testid="plans-tabs">
              {plans.map((p) => (
                <button key={p.id} onClick={() => setActiveId(p.id)} className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${active?.id === p.id ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`plan-tab-${p.id}`}>
                  {p.label}
                  {p.pin_count > 0 && <Badge variant="outline" className={`h-5 px-1.5 ${active?.id === p.id ? "border-white/40 text-white" : ""}`}>{p.pin_count}</Badge>}
                </button>
              ))}
            </div>

            {active && (
              <>
                <PlanViewer buildingId={buildingId} plan={active} doors={Array.isArray(doors.data) ? doors.data : []} canManage={canManage} onPinsChanged={reload} />
                {canManage && (
                  <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => deletePlan(active.id)} data-testid="plan-delete-btn">
                    <Trash2 className="mr-1.5 h-4 w-4" /> Delete this plan
                  </Button>
                )}
              </>
            )}
          </div>
        )
      )}

      <UploadPlanDialog open={dialogOpen} onOpenChange={setDialogOpen} buildingId={buildingId} onUploaded={() => { setDialogOpen(false); reload(); }} />
    </div>
  );
}
